// Configuration for the Movmo identity layer — the one-click ConnectMovmoButton
// chip (MovmoIdentityChip) on the Aero and Philippine Airlines demo landing pages.
//
// clientId / redirect path are FIXED, non-secret values. "aero-demo-airline" and
// "pa-demo-airline" are already provisioned on e2e as identity-partner OAuth
// clients (MOVMO-489/S3) with these EXACT registered redirect_uris (verified via
// monolith-api/scripts/movmo493-demo-airline-e2e.sh's header comment):
//
//   aero -> https://mockairline-e2e.app.flyeasyapp.com/aero/connect/callback
//   pa   -> https://mockairline-e2e.app.movmoapp.com/philippine-airlines/connect/callback
//
// Do NOT change these path constants without re-provisioning the OAuth client
// (which mints a new client_secret and breaks the demo-airline backend's stored
// secret — see docs/demo-airline.md in monolith-api). redirectUri is always built
// from window.location.origin + the path below so local dev / testing / e2e all
// resolve correctly as long as the path segment matches exactly.

export type DemoAirline = "aero" | "pa";

export const DEMO_AIRLINE_CLIENT_IDS: Record<DemoAirline, string> = {
    aero: "aero-demo-airline",
    pa: "pa-demo-airline",
};

// Path segment (from origin) that MUST exactly match the registered redirect_uri
// for each airline's OAuth client.
export const DEMO_AIRLINE_CALLBACK_PATHS: Record<DemoAirline, string> = {
    aero: "/aero/connect/callback",
    pa: "/philippine-airlines/connect/callback",
};

export function buildRedirectUri(airline: DemoAirline): string {
    return `${window.location.origin}${DEMO_AIRLINE_CALLBACK_PATHS[airline]}`;
}

// Each airline's OAuth client has its redirect_uri registered on EXACTLY ONE
// demo domain (aero → flyeasyapp.com, pa → movmoapp.com), but both domains
// serve this same app — so without a guard, the PA chip rendered on the Aero
// domain builds a redirect_uri the auth server rejects. Each skin's chip
// therefore renders only on its canonical origin (plus localhost for dev,
// where the OAuth connect flow is expected to fail — no localhost redirect
// URIs are registered for the demo clients).
export const DEMO_AIRLINE_CANONICAL_ORIGINS: Record<DemoAirline, string> = {
    aero: "https://mockairline-e2e.app.flyeasyapp.com",
    pa: "https://mockairline-e2e.app.movmoapp.com",
};

export function isCanonicalOriginFor(airline: DemoAirline): boolean {
    const origin = window.location.origin;
    if (origin === DEMO_AIRLINE_CANONICAL_ORIGINS[airline]) return true;
    return (
        origin.startsWith("http://localhost") ||
        origin.startsWith("http://127.0.0.1")
    );
}
